import {
  CellValues,
  ColumnHeading,
  Notice,
  PaginatedDataTable,
  TableCellStylesFn,
  useNetworkState,
  useVisibleHeadings,
} from '@lbcamden/lib-react';
import { formatDMY } from '@lbcamden/shared-utils';
import { FunctionComponent } from 'react';

import { useWorksOrdersList } from '~/client/hooks/storeHooks';
import { api } from '~/client/lib/trpc';
import { WorksOrder } from '~/common/types/worksOrders';

type Props = {
  propertyId: number;
  serviceRequestId: number;
};

type ColumnId = StringKey<WorksOrder>;

const headings = [
  { id: 'worksOrderId', content: 'ID' },
  { id: 'description', content: 'Description', noWrap: false },
  { id: 'status', content: 'Status' },
  { id: 'raisedDate', content: 'Raised' },
] as const satisfies ColumnHeading<ColumnId>[];

const cellStyles: TableCellStylesFn<ColumnId> = (columnId) => ({
  width: columnId === 'description' ? 200 : 50,
  maxWidth: columnId === 'description' ? 'auto' : 70,
});

const mapWorksOrderToRow = (worksOrder: WorksOrder): CellValues<ColumnId> => ({
  ...worksOrder,
  _rowId: String(worksOrder.worksOrderId),
  raisedDate: formatDMY(worksOrder.raisedDate),
});

const issueNoticeTitle = (hasResults: boolean) =>
  `Failed to retrieve${hasResults ? ' up to date ' : ' '}works orders`;

const issueNoticeMessage = (isOnline: boolean) =>
  isOnline
    ? 'An unknown error occurred. Please try again later'
    : 'Please check your network connection';

/**
 * Syncing all works orders upfront is too time consuming so they are synced
 * per service request on demand
 */
export const WorksOrders: FunctionComponent<Props> = ({ propertyId, serviceRequestId }) => {
  const { online } = useNetworkState();

  const { data: { success } = {}, isLoading } = api.syncWorksOrders.useQuery({
    propertyId,
    serviceRequestId,
  });

  const worksOrders = useWorksOrdersList(serviceRequestId);

  const hasIssue = !online || success === false;
  const hasResults = !!worksOrders.length;

  const visibleHeadings = useVisibleHeadings(headings, {
    xs: headings.slice(1, 3),
    sm: headings.slice(1, 3),
  });

  return (
    <>
      {hasIssue && (
        <Notice
          type={hasResults ? 'warning' : 'error'}
          title={issueNoticeTitle(hasResults)}
          message={issueNoticeMessage(!!online)}
        />
      )}
      <PaginatedDataTable
        css={{ padding: 0 }}
        cellStyles={cellStyles}
        headings={visibleHeadings}
        items={worksOrders}
        loading={isLoading && !hasResults}
        defaultSort={{ field: 'raisedDate', direction: 'desc' }}
        mapItemToRow={mapWorksOrderToRow}
      />
    </>
  );
};
